import {getCookie, setCookie} from '@github-ui/cookies'
import {useCallback, useState} from 'react'

import {persistPullsPreferences} from '@github-ui/shared-dashboard-surface/helpers/pulls-preferences-api'

const COOKIE_NAME = 'dashboard_surface_collapsed_sections'
const COOKIE_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000

let collapsedIds: Set<string> | null = null

/**
 * Test-only: clears the module-level collapsed-id cache so each test starts from
 * an unseeded state (the cache is intentionally shared across section hook
 * instances at runtime so toggles merge correctly).
 */
export function resetCollapsedIdsCacheForTests() {
  collapsedIds = null
}

function hasCollapsedCookie(): boolean {
  return getCookie(COOKIE_NAME) !== undefined
}

function parseCollapsedCookie(): Set<string> {
  const raw = getCookie(COOKIE_NAME)?.value ?? ''
  return new Set(
    raw
      .split(',')
      .map(s => s.trim())
      .filter(Boolean),
  )
}

/**
 * Seed the in-memory collapsed set from the server-resolved payload when no
 * cookie exists yet (e.g. the user cleared cookies). This keeps cookie-first
 * reads and writes working: without it, toggling one section would persist a
 * cookie built from an empty set and silently drop the other sections that the
 * server restored from the durable store.
 */
function seedCollapsedIdsFromPayload(sectionIds: readonly string[]) {
  if (collapsedIds === null && !hasCollapsedCookie()) {
    collapsedIds = new Set(sectionIds)
  }
}

function getCollapsedIds(): Set<string> {
  if (collapsedIds === null) {
    collapsedIds = parseCollapsedCookie()
  }
  return collapsedIds
}

function writeCollapsedIds(ids: Set<string>) {
  collapsedIds = ids
  const expireTime = new Date(Date.now() + COOKIE_EXPIRY_MS).toUTCString()
  setCookie(COOKIE_NAME, [...ids].join(','), expireTime)
  persistPullsPreferences({collapsed_sections: [...ids]})
}

/**
 * Hook to manage the collapsed state of an inbox section.
 *
 * Payload-as-truth: the server resolves collapsed sections cookie-first, so the
 * initial state is read from the resolved payload on both server and client.
 * Writes still go to the cookie (the server's fast read path) and the durable
 * store on every toggle.
 *
 * @param sectionId The inbox section ID (e.g. 'review-requested').
 * @param payloadCollapsedSections The server-resolved collapsed section ids from
 * the layout payload.
 */
export function useSaveCollapsedSection(sectionId: string, payloadCollapsedSections: readonly string[] = []) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // On the client, seed the in-memory set from the payload when no cookie
    // exists yet, so a later toggle writes a cookie that preserves the other
    // sections the server restored from the durable store.
    if (typeof document !== 'undefined' && !hasCollapsedCookie()) {
      seedCollapsedIdsFromPayload(payloadCollapsedSections)
    }
    return payloadCollapsedSections.includes(sectionId)
  })

  const setCollapsed = useCallback(
    (collapsed: boolean) => {
      const current = getCollapsedIds()
      // Match the old cookie-backed-set behavior: skip redundant cookie writes
      // and preference PUTs when the section is already in the requested state.
      if (current.has(sectionId) === collapsed) {
        return
      }

      setIsCollapsed(collapsed)

      const ids = new Set(current)
      if (collapsed) {
        ids.add(sectionId)
      } else {
        ids.delete(sectionId)
      }
      writeCollapsedIds(ids)
    },
    [sectionId],
  )

  return {isCollapsed, setCollapsed}
}
